/**
 * Base del pixel art procedural de La Oficina: rejilla lógica, contorno,
 * sombreado y el pincel con el que se pintan los muebles.
 * Un pixel lógico mide PX unidades de mundo.
 */

export const PX = 3;
export const INK = '#2a1c14';

/**
 * Entero estable a partir de un id. Termina con una mezcla de bits para que
 * ids consecutivos ("piso-1", "piso-2") den números sin relación entre sí.
 */
export function hashId(value) {
    const text = String(value || '');
    let hash = 0;
    for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
    hash = Math.imul(hash ^ (hash >>> 16), 0x85ebca6b);
    hash = Math.imul(hash ^ (hash >>> 13), 0xc2b2ae35);
    return (hash ^ (hash >>> 16)) >>> 0;
}

function channels(hex) {
    const value = parseInt(String(hex).replace('#', ''), 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex(r, g, b) {
    const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
    return `#${((1 << 24) + (clamp(r) << 16) + (clamp(g) << 8) + clamp(b)).toString(16).slice(1)}`;
}

/** Aclara u oscurece un color hex sumando a cada canal. */
export function shade(hex, amount) {
    const [r, g, b] = channels(hex);
    return toHex(r + amount, g + amount, b + amount);
}

/** Mezcla dos colores hex. t = 0 devuelve a, t = 1 devuelve b. */
export function mix(a, b, t) {
    const one = channels(a);
    const two = channels(b);
    return toHex(one[0] + (two[0] - one[0]) * t, one[1] + (two[1] - one[1]) * t, one[2] + (two[2] - one[2]) * t);
}

/** Color hex con transparencia. */
export function rgba(hex, alpha) {
    const [r, g, b] = channels(hex);
    return `rgba(${r},${g},${b},${alpha})`;
}

/** Rejilla de pixeles lógicos sobre la que se arma un sprite antes de cachearlo. */
export class Grid {
    constructor(w, h) {
        this.w = w;
        this.h = h;
        this.cells = new Array(w * h).fill(null);
    }

    set(x, y, color) {
        const cx = Math.round(x);
        const cy = Math.round(y);
        if (cx < 0 || cy < 0 || cx >= this.w || cy >= this.h) return;
        this.cells[cy * this.w + cx] = color;
    }

    rect(x, y, w, h, color) {
        const x0 = Math.round(x);
        const y0 = Math.round(y);
        for (let row = 0; row < h; row += 1) {
            for (let col = 0; col < w; col += 1) this.set(x0 + col, y0 + row, color);
        }
    }

    /** Trazo grueso entre dos puntos; sirve para brazos, piernas y torso. */
    line(x0, y0, x1, y1, thick, color) {
        const steps = Math.max(1, Math.ceil(Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0))));
        for (let i = 0; i <= steps; i += 1) {
            const px = x0 + ((x1 - x0) * i) / steps;
            const py = y0 + ((y1 - y0) * i) / steps;
            this.rect(Math.round(px - thick / 2), Math.round(py - thick / 2), thick, thick, color);
        }
    }

    /** Contorno de un pixel alrededor de la silueta. */
    outline(color) {
        const marks = [];
        for (let y = 0; y < this.h; y += 1) {
            for (let x = 0; x < this.w; x += 1) {
                if (this.cells[y * this.w + x]) continue;
                const filled = (dx, dy) => {
                    const nx = x + dx;
                    const ny = y + dy;
                    return nx >= 0 && ny >= 0 && nx < this.w && ny < this.h && this.cells[ny * this.w + nx];
                };
                if (filled(1, 0) || filled(-1, 0) || filled(0, 1) || filled(0, -1)) marks.push(y * this.w + x);
            }
        }
        marks.forEach((index) => {
            this.cells[index] = color;
        });
    }

    toCanvas() {
        const canvas = document.createElement('canvas');
        canvas.width = this.w;
        canvas.height = this.h;
        const ctx = canvas.getContext('2d');
        for (let y = 0; y < this.h; y += 1) {
            let x = 0;
            while (x < this.w) {
                const color = this.cells[y * this.w + x];
                if (!color) {
                    x += 1;
                    continue;
                }
                let run = 1;
                while (x + run < this.w && this.cells[y * this.w + x + run] === color) run += 1;
                ctx.fillStyle = color;
                ctx.fillRect(x, y, run, 1);
                x += run;
            }
        }
        return canvas;
    }
}

/**
 * Pincel de muebles. El origen (x0, y0) es el centro de la base; `r` recibe
 * la x desde ese centro y la altura del borde superior medida hacia arriba,
 * todo en pixeles lógicos.
 */
export function painter(ctx, x0, y0) {
    const ox = Math.round(x0);
    const oy = Math.round(y0);
    return {
        ctx,
        x: ox,
        y: oy,
        r(x, top, w, h, color) {
            ctx.fillStyle = color;
            ctx.fillRect(ox + Math.round(x * PX), oy - Math.round(top * PX), Math.round(w * PX), Math.round(h * PX));
        },
        /** Marco de un pixel lógico. */
        box(x, top, w, h, color) {
            this.r(x, top, w, 1, color);
            this.r(x, top - h + 1, w, 1, color);
            this.r(x, top, 1, h, color);
            this.r(x + w - 1, top, 1, h, color);
        },
    };
}
